import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import * as moment from 'moment';
import { Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { AppStateInterface } from '../../appState.interface';
import { CurrentUserInfo } from '../models/currentUserInfo';
import * as PostActions from '../store/actions';
import { SelectedCustomerCacheInterface } from '../store/interface/selectedCustomerCache.interface';
import { SelectedPeriodFitler } from '../store/interface/selectedPeriod.interface';
import {
  SET_VEHICLE_FILTER_INCLUDE_CHILDREN,
  SET_VEHICLE_FILTER_ALSO_DISABLED,
  SET_VEHICLE_FILTER_ALSO_DATA_HIDDEN,
  VEHICLE_FILTER_SUBSCRIBED,
  SET_VEHICLE_FILTER_HAVE_COMMUNICATED
} from '../config/config.constants';
import { CatalogService } from './catalog.service';
import * as CatalogActions from '../store/catalog/catalog.actions';
import { UtilsService } from './utils.service';

/**
 * Servizio centralizzato per l'inizializzazione dello stato dell'applicazione.
 * Evita duplicazione di codice tra APP_INITIALIZER e login flow.
 */
@Injectable({
  providedIn: 'root'
})
export class StateInitializerService {

  constructor(
    private store: Store<AppStateInterface>,
    private catalogService: CatalogService,
    private utilsService: UtilsService
  ) {}

  /**
   * Inizializza completamente lo store con i dati dell'utente.
   * Questo metodo viene chiamato sia dall'APP_INITIALIZER (reload con token)
   * che dal login.component (dopo login fresco).
   *
   * @param userInfo - Informazioni utente da CURRENT_USER_ENDPOINT
   * @returns L'oggetto customerInfo costruito per lo store
   */
  initializeStoreState(userInfo: CurrentUserInfo): SelectedCustomerCacheInterface {
    console.log('🏪 [StateInitializer] Initializing store state...');

    // Costruisci oggetto customer per lo store
    const customerInfo: SelectedCustomerCacheInterface = {
      isVirtual: false,
      isFieldTest: false,
      isSubscriptionEnded: false,
      subscriptionEndDate: null,
      vatCode: null,
      fiscalCode: null,
      contactName: null,
      contactPhone1: null,
      contactPhone2: null,
      contactEmail: null,
      contactAddress: null,
      logo: null,
      uidCountry: '',
      uidLegalRule: '',
      groups: [],
      features: [],
      children: [],
      isTachoLight: false,
      maxVehicles: null,
      name: userInfo.customer?.name || '',
      uidParent: userInfo.customer?.uidParent || '',
      isHardLocked: userInfo.customer?.isHardLocked || false,
      uid: userInfo.customer?.uid || '',
      nameWithChildren: '',
      timezone: (userInfo.customer as any)?.timezone || null
    };

    // Dispatch user info nello store
    this.store.dispatch(PostActions.setUser(userInfo));

    // Carica lista clienti
    this.store.dispatch(PostActions.getCustomers());

    // Inizializza i filtri veicoli con i valori di default
    this.store.dispatch(PostActions.setVehicleFilters({
      includeChildren: SET_VEHICLE_FILTER_INCLUDE_CHILDREN,
      alsoDisabled: SET_VEHICLE_FILTER_ALSO_DISABLED,
      alsoDataHidden: SET_VEHICLE_FILTER_ALSO_DATA_HIDDEN,
      subscribed: VEHICLE_FILTER_SUBSCRIBED,
      haveCommunicated: SET_VEHICLE_FILTER_HAVE_COMMUNICATED
    }));

    // Se esiste uno stato salvato in localStorage con un customer selezionato,
    // usarlo invece del default API (es. su reload pagina dopo aver cambiato customer).
    // Sul login fresco localStorage è già stato svuotato da LoginComponent.ngOnInit().
    let effectiveCustomerInfo = customerInfo;
    const savedState = localStorage.getItem('state');
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        const savedCustomer = parsed.applySelectedCustomer;
        if (savedCustomer?.uid) {
          effectiveCustomerInfo = savedCustomer;
          console.log('🔄 [StateInitializer] Restoring saved customer from localStorage:', savedCustomer.uid);
        }
      } catch {
        // parse fallita, usa il customer di default dall'API
      }
    }

    // Imposta customer selezionato
    this.store.dispatch(PostActions.setSelectedCustomerCache(effectiveCustomerInfo));
    this.store.dispatch(PostActions.setApplySelectedCustomer(effectiveCustomerInfo));

    // Carica veicoli e drivers del customer
    this.store.dispatch(PostActions.getVehiclesOfCustomers());
    this.store.dispatch(PostActions.getDriversOfCustomers());

    // Inizializza periodo selezionato (oggi)
    const currentPeriod: SelectedPeriodFitler = {
      fromDtPicker: moment(new Date()).valueOf(),
      toDtPicker: moment(new Date()).valueOf(),
      timespanSel: 'timeperiod',
      isApply: false
    };
    this.store.dispatch(PostActions.setSelectedPediod(currentPeriod));
    this.store.dispatch(PostActions.setApplySelectedPediod(currentPeriod));

    console.log('✅ [StateInitializer] Store initialization completed');

    return customerInfo;
  }

  /**
   * Carica il catalogo dei segnali per il customer specificato.
   * Questo metodo viene chiamato sia dall'APP_INITIALIZER che dal login
   * (solo per ambienti non dev/test).
   *
   * @param userId - UID dell'utente per cui caricare il catalogo
   * @param customerId - UID del customer per cui caricare il catalogo
   */
  loadSignalsCatalog(): void {
    console.log('📡 [StateInitializer] Loading signals catalog...');
    this.store.dispatch(CatalogActions.loadCatalog());
  }

  /**
   * Carica e applica la configurazione grafica (logo, colore primario, titolo portale).
   * Questo metodo deve essere chiamato DOPO il login e PRIMA della navigazione al portale,
   * per garantire che le personalizzazioni visive siano applicate prima che l'utente
   * acceda alla sezione autenticata.
   *
   * @param userId - UID dell'utente
   * @param customerId - UID del customer
   * @returns Observable che completa quando la configurazione è stata applicata
   */
  loadAndApplyGraphicsConfiguration(userId: string, customerId: string): Observable<void> {
    console.log('🎨 [StateInitializer] Loading graphics configuration...');

    return this.catalogService.getGraphicsConfigFromAPI(customerId, userId).pipe(
      map(configs => {
        if (configs && configs.length > 0) {
          // Prendi la configurazione più recente
          const latestConfig = configs[0];
          const graphicsData = latestConfig?.config?.mappingData;

          if (graphicsData) {
            console.log('🎨 [StateInitializer] Graphics configuration found:', graphicsData);

            // Applica il colore primario
            if (graphicsData.primaryColor) {
              localStorage.setItem('actia-portal-primary-color', graphicsData.primaryColor);
              this.utilsService.applyThemeColorVars(graphicsData.primaryColor);
              console.log('🎨 [StateInitializer] Primary color applied:', graphicsData.primaryColor);
            } else {
              localStorage.removeItem('actia-portal-primary-color');
            }

            // Applica il logo (salvato in localStorage per accesso globale)
            if (graphicsData.logo) {
              localStorage.setItem('actia-portal-logo', graphicsData.logo);
              console.log('🎨 [StateInitializer] Portal logo stored');
            } else {
              localStorage.removeItem('actia-portal-logo');
            }

            // Applica il titolo del portale
            if (graphicsData.portalTitle) {
              document.title = graphicsData.portalTitle;
              console.log('🎨 [StateInitializer] Portal title applied:', graphicsData.portalTitle);
            }

            console.log('✅ [StateInitializer] Graphics configuration applied successfully');
          } else {
            console.log('ℹ️ [StateInitializer] No graphics data found in configuration');
            localStorage.removeItem('actia-portal-primary-color');
            localStorage.removeItem('actia-portal-logo');
          }
        } else {
          console.log('ℹ️ [StateInitializer] No graphics configuration found, using defaults');
          localStorage.removeItem('actia-portal-primary-color');
          localStorage.removeItem('actia-portal-logo');
        }
      }),
      catchError(error => {
        console.warn('⚠️ [StateInitializer] Error loading graphics configuration, using defaults:', error);
        // Non bloccare il login in caso di errore, continua con i valori di default
        return of(void 0);
      })
    );
  }
}
